import { useEffect, useState } from "react";
import { defaultBookshelfGroupId } from "@shared/bookshelf";
import {
  addToBookshelf,
  addSourceBookToBookshelf,
  getBookshelfView,
  getSession,
  login,
  logout,
  removeFromBookshelf,
  setBookshelfActiveSource,
  setBookshelfGroup,
  type BookshelfViewItem,
  type ContentKind,
  type SearchResult,
  type UserSpace,
} from "../api";
import { useBackupWorkspace } from "../backup/backup-workspace";
import { useBookshelfWorkspace } from "../shelf/bookshelf-workspace";
import { useReaderChapterWorkspace } from "./reader-chapter-workspace";
import {
  createReaderOpenRequest,
  isBookshelfViewItem,
  resolveRecentReadingAction,
  sortBookshelfView,
} from "./reader-library";
import { createFallbackBookSource, isReaderContentSnapshotForTarget } from "./reader-session";
import type { ReaderContentSnapshot } from "./reader-session";
import { useReaderSessionWorkspace } from "./reader-session-workspace";
import { useReaderPreferenceWorkspace } from "./reader-preference-workspace";
import { useSourceDashboardWorkspace } from "../sources/source-dashboard-workspace";
import { navigateTo } from "../routes";

const defaultLibraryKind: ContentKind = "novel";

export function useReaderWorkspace() {
  const [user, setUser] = useState<UserSpace | null>(null);
  const [activeKind, setActiveKind] = useState<ContentKind>(defaultLibraryKind);
  const [authReady, setAuthReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("正在连接阅读服务");
  const readerSession = useReaderSessionWorkspace();
  const preferenceWorkspace = useReaderPreferenceWorkspace({ setMessage });
  const sourceDashboard = useSourceDashboardWorkspace({
    user,
    setBusy,
    setMessage,
    onSourcesChanged: () => bookshelfWorkspace.resetSearchResults(),
  });
  const bookshelfWorkspace = useBookshelfWorkspace({
    user,
    activeKind,
    setBusy,
    setMessage,
  });
  const backupWorkspace = useBackupWorkspace({
    user,
    activeKind,
    setUser,
    setBookshelf: bookshelfWorkspace.setBookshelf,
    setBusy,
    setMessage,
    refreshMonitor: sourceDashboard.refreshMonitor,
  });
  const readerChapter = useReaderChapterWorkspace({
    activeKind,
    setActiveKind,
    user,
    prefetchChapterCount: preferenceWorkspace.preference.prefetchChapterCount,
    setBusy,
    setMessage,
    writeReaderSession: readerSession.writeReaderSession,
    writeReaderContentSnapshot: readerSession.writeContentSnapshot,
    setBookshelfForKind: async (userId, kind) => {
      await bookshelfWorkspace.loadBookshelf(userId, kind);
    },
    patchBookshelfProgress: bookshelfWorkspace.patchBookshelfProgress,
    refreshMonitor: sourceDashboard.refreshMonitor,
  });

  useEffect(() => {
    void bootstrap();
  }, []);

  useEffect(() => {
    if (!user) return;
    void bookshelfWorkspace.loadBookshelf(user.id, activeKind);
  }, [user?.id, activeKind]);

  async function bootstrap() {
    readerSession.initializeRecentReading();
    const localSnapshot = restoreReaderFromLocalSnapshot();
    if (localSnapshot) setAuthReady(true);

    try {
      const session = await getSession();
      setUser(session.user);
      await initializeSession(session, { userReady: true });
      setAuthReady(true);
    } catch (error) {
      if (localSnapshot) {
        setMessage("离线阅读中，已使用本地缓存");
        setAuthReady(true);
        return;
      }

      setUser(null);
      preferenceWorkspace.clearPreferenceSession();
      setMessage(error instanceof Error && error.message !== "请先登录" ? error.message : "请登录");
      setAuthReady(true);
    }
  }

  async function initializeSession(session: Awaited<ReturnType<typeof getSession>>, options: { userReady?: boolean } = {}) {
    if (!options.userReady) setUser(session.user);
    readerSession.initializeRecentReading();

    const shelf = await runSessionStartupStep("正在同步书架", async () => {
      const loadedShelf = sortBookshelfView(await getBookshelfView(session.user.id, activeKind));
      bookshelfWorkspace.setBookshelf(loadedShelf);
      return loadedShelf;
    }, [] as BookshelfViewItem[]);

    await runSessionStartupStep("正在同步阅读偏好", () => preferenceWorkspace.initializePreference(
      session.user.id,
      session.activePreference,
      session.preferences,
    ));
    await runSessionStartupStep("正在同步书源状态", () => sourceDashboard.initializeSources(session.user.id, session.sources));
    await runSessionStartupStep("正在恢复阅读现场", () => restoreReaderFromSession(session.user.id, shelf));
  }

  function restoreReaderFromLocalSnapshot(): ReaderContentSnapshot | null {
    if (window.location.pathname !== "/reader") return null;

    const snapshot = readerSession.readContentSnapshot();
    if (!snapshot) return null;

    setUser(snapshot.user ?? createOfflineReaderUser(snapshot.userId));
    setActiveKind(snapshot.target.kind);
    readerChapter.restoreReaderSnapshot(snapshot);
    readerSession.writeContentSnapshot(snapshot);
    setMessage("已从本地缓存恢复阅读现场");
    return snapshot;
  }

  async function runSessionStartupStep<T>(message: string, action: () => Promise<T>, fallback: T): Promise<T>;
  async function runSessionStartupStep(message: string, action: () => Promise<void>): Promise<void>;
  async function runSessionStartupStep<T>(message: string, action: () => Promise<T>, fallback?: T): Promise<T | void> {
    setMessage(message);

    try {
      return await action();
    } catch (error) {
      // 启动恢复失败不应把已确认登录的用户挡在检查页
      setMessage(error instanceof Error ? error.message : String(error));
      return fallback;
    }
  }

  async function handleLogin(username: string, password: string) {
    setBusy(true);
    setMessage("正在登录");

    try {
      await initializeSession(await login(username, password));
    } finally {
      setBusy(false);
    }
  }

  async function handleLogout() {
    await logout();
    setUser(null);
    readerSession.clearReaderSession();
    preferenceWorkspace.clearPreferenceSession();
    bookshelfWorkspace.setBookshelf([]);
    bookshelfWorkspace.setResults([]);
    setMessage("请登录");
    navigateTo("/shelf");
  }

  async function handleOpen(result: SearchResult | BookshelfViewItem, chapterId?: string) {
    if (!user) return;
    const shelfItem = isBookshelfViewItem(result) ? result : bookshelfWorkspace.bookshelf.find(item => item.book.id === result.book.id);
    let request = createReaderOpenRequest({
      userId: user.id,
      item: shelfItem ?? result,
      selectedSourceByBook: bookshelfWorkspace.selectedSourceByBook,
      chapterId,
    });

    if (!request) return;

    readerChapter.prepareReaderOpen({ title: request.title, shouldNavigate: request.shouldNavigate });

    if (!shelfItem && !isBookshelfViewItem(result)) {
      setBusy(true);
      setMessage("正在加入书架并打开章节");

      try {
        const persistedItem = await ensureSearchResultInBookshelf(user.id, result, request.source.sourceId);
        request = createReaderOpenRequest({
          userId: user.id,
          item: persistedItem,
          selectedSourceByBook: bookshelfWorkspace.selectedSourceByBook,
          chapterId,
        });
        if (!request) throw new Error("书籍已保存但无法打开");
      } catch (error) {
        readerChapter.cancelReaderOpen();
        setBusy(false);
        setMessage(error instanceof Error ? error.message : String(error));
        return;
      }
    }

    if (shelfItem) {
      await syncBookshelfActiveSource(user.id, shelfItem, request.source.sourceId);
    }

    await readerChapter.openReaderTarget(request);
  }

  async function handleOpenRecentReading() {
    const recentReading = readerSession.recentReading;
    if (!user || !recentReading) return;
    const action = resolveRecentReadingAction(recentReading, bookshelfWorkspace.bookshelf);

    if (action.type === "search") {
      setMessage("最近阅读不在书架，正在在线搜索");
      if (action.kind !== activeKind) {
        setActiveKind(action.kind);
        await bookshelfWorkspace.loadBookshelf(user.id, action.kind);
      }
      await bookshelfWorkspace.searchByKeyword(action.keyword, action.kind);
      return;
    }

    await syncBookshelfActiveSource(user.id, action.item, action.source.sourceId);
    await readerChapter.openReaderTarget({
      userId: user.id,
      bookId: action.item.book.id,
      title: action.item.book.title,
      kind: action.item.book.kind as ContentKind,
      source: action.source,
      chapterId: recentReading.chapterId,
      initialParagraphIndex: recentReading.paragraphIndex,
      initialScrollRatio: recentReading.scrollRatio,
      shouldNavigate: true,
    });
  }

  async function restoreReaderFromSession(userId: string, shelf: BookshelfViewItem[]) {
    const target = readerSession.readReaderSession();

    if (!target && window.location.pathname === "/reader") {
      setMessage("没有可恢复的阅读进度，已返回书架");
      navigateTo("/shelf");
      return;
    }

    if (!target || window.location.pathname !== "/reader") {
      setMessage("书架已同步");
      return;
    }

    const shelfItem = shelf.find(item => item.book.id === target.bookId);
    const source =
      shelfItem?.sources.find(item => item.sourceId === target.sourceId && item.sourceBookId === target.sourceBookId) ??
      createFallbackBookSource(target);
    const snapshot = readerSession.readContentSnapshot();

    if (isReaderContentSnapshotForTarget(snapshot, userId, target)) {
      readerChapter.restoreReaderSnapshot(snapshot);
      setMessage("阅读现场已恢复");
      return;
    }

    await readerChapter.openReaderTarget({
      userId,
      bookId: shelfItem?.book.id ?? target.bookId,
      title: shelfItem?.book.title ?? target.title,
      kind: (shelfItem?.book.kind as ContentKind | undefined) ?? target.kind,
      source,
      chapterId: target.chapterId,
      initialParagraphIndex: target.paragraphIndex,
      initialScrollRatio: target.scrollRatio,
      shouldNavigate: false,
    });
  }

  async function ensureSearchResultInBookshelf(userId: string, result: SearchResult, sourceId: string): Promise<BookshelfViewItem> {
    const source = result.sources.find(item => item.sourceId === sourceId) ?? result.sources[0];
    if (!source) throw new Error("当前搜索结果没有可用书源");

    let persistedItem: BookshelfViewItem | undefined;

    try {
      await addToBookshelf(userId, result.book.id, sourceId, defaultBookshelfGroupId);
    } catch {
      // discover 结果由前端临时构造 需要先按书源详情落库再加入书架
      persistedItem = await addSourceBookToBookshelf(userId, source.sourceId, source.sourceBookId, defaultBookshelfGroupId);
    }

    bookshelfWorkspace.setResults([]);
    const shelf = sortBookshelfView(await getBookshelfView(userId, result.book.kind as ContentKind));
    bookshelfWorkspace.setBookshelf(shelf);
    const resolvedItem = persistedItem ??
      shelf.find(item => item.book.id === result.book.id) ??
      shelf.find(item => item.sources.some(itemSource => itemSource.sourceId === source.sourceId && itemSource.sourceBookId === source.sourceBookId));

    if (!resolvedItem) throw new Error("书籍已加入书架但未能定位");
    return resolvedItem;
  }

  async function syncBookshelfActiveSource(userId: string, item: BookshelfViewItem, sourceId: string) {
    if (item.item.activeSourceId === sourceId) return;
    await setBookshelfActiveSource(userId, item.book.id, sourceId);
    bookshelfWorkspace.patchBookshelfActiveSource(item.book.id, sourceId);
  }

  function handleSourceSelect(bookId: string, sourceId: string) {
    bookshelfWorkspace.handleSourceSelect(bookId, sourceId);
    const shelfItem = bookshelfWorkspace.bookshelf.find(item => item.book.id === bookId);

    if (!user || !shelfItem) return;

    bookshelfWorkspace.patchBookshelfActiveSource(bookId, sourceId);
    void setBookshelfActiveSource(user.id, bookId, sourceId).catch(async error => {
      setMessage(error instanceof Error ? error.message : String(error));
      bookshelfWorkspace.setBookshelf(sortBookshelfView(await getBookshelfView(user.id, activeKind)));
    });
  }

  async function handleMoveBookToGroup(bookId: string, groupId: string) {
    if (!user) return;

    bookshelfWorkspace.patchBookshelfGroup(bookId, groupId);

    try {
      await setBookshelfGroup(user.id, bookId, groupId);
      setMessage("分组已更新");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : String(error));
      bookshelfWorkspace.setBookshelf(sortBookshelfView(await getBookshelfView(user.id, activeKind)));
    }
  }

  async function handleDeleteBookFromShelf(bookId: string) {
    if (!user) return;

    bookshelfWorkspace.removeBookshelfItem(bookId);

    try {
      await removeFromBookshelf(user.id, bookId);
      setMessage("已从书架删除");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : String(error));
      bookshelfWorkspace.setBookshelf(sortBookshelfView(await getBookshelfView(user.id, activeKind)));
    }
  }

  async function handleAddSearchResultToShelf(result: SearchResult) {
    if (!user) return;
    const activeSourceId = bookshelfWorkspace.selectedSourceByBook[result.book.id] ?? result.sources[0]?.sourceId;
    const source = result.sources.find(item => item.sourceId === activeSourceId) ?? result.sources[0];

    if (!activeSourceId || !source) {
      setMessage("当前搜索结果没有可用书源");
      return;
    }

    try {
      await addToBookshelf(user.id, result.book.id, activeSourceId, defaultBookshelfGroupId);
    } catch {
      // discover 结果可能还没有服务端 bookId 需要通过书源详情创建真实书籍
      await addSourceBookToBookshelf(user.id, source.sourceId, source.sourceBookId, defaultBookshelfGroupId);
    }

    await bookshelfWorkspace.loadBookshelf(user.id, result.book.kind as ContentKind);
    setMessage("已添加到书架");
  }

  async function handleRemoveSearchResultFromShelf(bookId: string) {
    if (!user) return;
    await removeFromBookshelf(user.id, bookId);
    await bookshelfWorkspace.loadBookshelf(user.id, activeKind);
    setMessage("已从书架删除");
  }

  function handleToggleTheme() {
    // 全局主题复用阅读偏好里的 theme 字段 所有页面共用一套状态
    void preferenceWorkspace.handlePreferenceChange({
      theme: preferenceWorkspace.preference.theme === "dark" ? "light" : "dark",
    });
  }

  return {
    user,
    authReady,
    sources: sourceDashboard.sources,
    enabledSourceIds: sourceDashboard.enabledSourceIds,
    sourceStatuses: sourceDashboard.sourceStatuses,
    sourceDiscoverById: sourceDashboard.sourceDiscoverById,
    sourceHomeDiscoverById: sourceDashboard.sourceHomeDiscoverById,
    sourceGroups: sourceDashboard.sourceGroups,
    discoverLoadingSourceId: sourceDashboard.discoverLoadingSourceId,
    sourceActionBusyId: sourceDashboard.sourceActionBusyId,
    telemetry: sourceDashboard.telemetry,
    bookshelf: bookshelfWorkspace.bookshelf,
    query: bookshelfWorkspace.query,
    results: bookshelfWorkspace.results,
    searchMessage: bookshelfWorkspace.searchMessage,
    searching: bookshelfWorkspace.searching,
    opened: readerChapter.opened,
    openedChapters: readerChapter.openedChapters,
    activeChapterId: readerChapter.activeChapterId,
    readerOpeningTitle: readerChapter.readerOpeningTitle,
    pendingShelfConfirmation: readerChapter.pendingShelfConfirmation,
    selectedBookTitle: readerChapter.selectedBookTitle,
    readerTarget: readerChapter.readerTarget,
    recentReading: readerSession.recentReading,
    restoreParagraphIndex: readerChapter.restoreParagraphIndex,
    restoreScrollRatio: readerChapter.restoreScrollRatio,
    selectedSourceByBook: bookshelfWorkspace.selectedSourceByBook,
    backupText: backupWorkspace.backupText,
    preference: preferenceWorkspace.preference,
    busy,
    message,
    setQuery: bookshelfWorkspace.handleQueryChange,
    setBackupText: backupWorkspace.setBackupText,
    handleSearch: bookshelfWorkspace.handleSearch,
    handleStopSearch: bookshelfWorkspace.handleStopSearch,
    handleOpen,
    handleOpenRecentReading,
    handleSourceSelect,
    handleRemoveSearchResult: bookshelfWorkspace.handleRemoveSearchResult,
    handleAddSearchResultToShelf,
    handleRemoveSearchResultFromShelf,
    handleMoveBookToGroup,
    handleDeleteBookFromShelf,
    handleClearSearchResults: bookshelfWorkspace.handleClearSearchResults,
    handleExportBackup: backupWorkspace.handleExportBackup,
    handleImportBackup: backupWorkspace.handleImportBackup,
    handlePreferenceChange: preferenceWorkspace.handlePreferenceChange,
    handleToggleTheme,
    handleLogin,
    handleLogout,
    handleOpenChapterFromDirectory: readerChapter.handleOpenChapterFromDirectory,
    handleOpenAdjacentChapter: readerChapter.handleOpenAdjacentChapter,
    handleActiveChapterChange: readerChapter.handleActiveChapterChange,
    handleLeaveReader: readerChapter.handleLeaveReader,
    handleReaderReturned: readerChapter.handleReaderReturned,
    handleSaveCurrentProgress: readerChapter.handleSaveCurrentProgress,
    handleReturnToShelf: readerChapter.handleReturnToShelf,
    handleToggleSource: sourceDashboard.handleToggleSource,
    handleCheckSource: sourceDashboard.handleCheckSource,
    handleLoadSourceDiscover: sourceDashboard.handleLoadSourceDiscover,
    handleSaveSourceGroups: sourceDashboard.handleSaveSourceGroups,
  };
}

export type ReaderWorkspace = ReturnType<typeof useReaderWorkspace>;

function createOfflineReaderUser(userId: string): UserSpace {
  const now = new Date(0).toISOString();

  return {
    id: userId,
    username: "offline",
    displayName: "离线阅读",
    role: "user",
    status: "active",
    groupIds: [],
    sourceIds: [],
    createdAt: now,
    updatedAt: now,
  };
}
